"use client";

import { useEffect, useRef, useState } from "react";
import { FiInfo } from "react-icons/fi";
import QuestionBlock from "./questionBlock";
import StatusPopUp from "../../popup/statusPopUp";
import { buildGameSuccessStatus } from "../shared/gameStatusMessages";
import ConfirmPopUp from "../../popup/confirmPopUp";
import completeTheSentence from "../../../services/completeTheSentence/completeTheSentence";
import uploadAPI from "../../../services/upload/uploadAPI";
import mainAPI from "../../../services/main/mainAPI";
import { collectPersistedIdsFromApiRows } from "../shared/serverPersistedIds";
import { GamePreviewModal } from "../shared/GamePreviewModal";

const choiceLabels = ["ก", "ข", "ค", "ง", "จ", "ฉ", "ช", "ซ", "ฌ", "ญ", "ฎ", "ฏ"];

type Answer = {
    id: number;
    text: string;
    isCorrect: boolean;
    imageFileName: string;
    imageFile: File | null;
    imageUrl: string | null;
    audioFileName: string;
    audioFile: File | null;
    audioUrl: string | null;
};

type Question = {
    id: number;
    text: string;
    skill_listening_speaking: boolean;
    skill_lang_nature: boolean;
    skill_reading: boolean;
    skill_writing: boolean;
    hasHint: boolean;
    hintText: string;
    hintImageFileName: string;
    hintImageFile: File | null;
    hintImageUrl: string | null;
    hintAudioFileName: string;
    hintAudioFile: File | null;
    hintAudioUrl: string | null;
    imageFileName: string;
    imageFile: File | null;
    imageUrl: string | null;
    audioFileName: string;
    audioFile: File | null;
    audioUrl: string | null;
    answers: Answer[];
};

type QuestionSkillKey =
    | "skill_listening_speaking"
    | "skill_lang_nature"
    | "skill_reading"
    | "skill_writing";

type QuestionSkillFlags = {
    skill_listening_speaking: boolean;
    skill_lang_nature: boolean;
    skill_reading: boolean;
    skill_writing: boolean;
};

type GameAudios = {
    audio_question: boolean;
    audio_choice: boolean;
    audio_assistant_voice: boolean;
    audio_background: boolean;
};

type GameAudiosKey = keyof GameAudios;

const DEFAULT_GAME_AUDIOS: GameAudios = {
    audio_question: false,
    audio_choice: false,
    audio_assistant_voice: false,
    audio_background: false,
};

type UploadTarget = {
    key: string;
    file: File;
    fileName: string;
    sectionName: string;
    refId: string;
};

type CompleteTheSentenceProps = {
    createdGameData?: {
        id: number | string;
        exercise_name?: string;
        uuid?: string;
        description?: string | null;
    } | null;
    initialGameDetail?: {
        game_info?: {
            id?: number | string;
            uuid?: string;
            exercise_name?: string;
            suggestion?: string | null;
            description?: string | null;
        };
        questions?: Array<{
            id?: number;
            no?: number;
            question?: string;
            hint?: string | null;
            image_question?: string | null;
            sound_question?: string | null;
            image_hint?: string | null;
            sound_hint?: string | null;
            skill_listening_speaking?: boolean | "true" | "false" | null;
            skill_lang_nature?: boolean | "true" | "false" | null;
            skill_reading?: boolean | "true" | "false" | null;
            skill_writing?: boolean | "true" | "false" | null;
            audio_question?: boolean | "true" | "false" | null;
            audio_choice?: boolean | "true" | "false" | null;
            audio_assistant_voice?: boolean | "true" | "false" | null;
            audio_background?: boolean | "true" | "false" | null;
            choices?: Array<{
                id?: number;
                choice?: string;
                is_correct?: boolean;
                image_choice?: string | null;
                sound_choice?: string | null;
            }>;
        }>;
    } | null;
    onCompleted?: () => void;
};

function getFileExtension(fileName: string) {
    const dotIndex = fileName.lastIndexOf(".");
    if (dotIndex < 0 || dotIndex === fileName.length - 1) {
        return "";
    }
    return fileName.slice(dotIndex);
}

function getFileNameFromPath(path: string | null | undefined) {
    if (!path) {
        return "";
    }

    const cleanPath = String(path);
    const bySlash = cleanPath.split("/");
    const candidate = bySlash[bySlash.length - 1] || "";
    return decodeURIComponent(candidate);
}

function buildUploadedMediaUrl(path: string | null | undefined) {
    if (!path) {
        return null;
    }

    const raw = String(path).trim();
    if (!raw) {
        return null;
    }

    if (raw.startsWith("http://") || raw.startsWith("https://")) {
        return raw;
    }

    const baseUrl = process.env.NEXT_PUBLIC_BACKEND_URL || "https://thaipilacreate.gforcesolution.com";
    const normalizedBaseUrl = baseUrl.replace(/\/$/, "");
    return `${normalizedBaseUrl}/api/${encodeURIComponent(raw)}`;
}

function normalizeSkillBoolean(value: unknown) {
    if (typeof value === "boolean") {
        return value;
    }

    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized === "true") {
            return true;
        }
        if (normalized === "false") {
            return false;
        }
    }

    return false;
}

function extractQuestionSkills(question: Pick<Question, QuestionSkillKey>): QuestionSkillFlags {
    return {
        skill_listening_speaking: normalizeSkillBoolean(question.skill_listening_speaking),
        skill_lang_nature: normalizeSkillBoolean(question.skill_lang_nature),
        skill_reading: normalizeSkillBoolean(question.skill_reading),
        skill_writing: normalizeSkillBoolean(question.skill_writing),
    };
}

function extractGameAudiosFromUnknown(value: Partial<Record<GameAudiosKey, unknown>>): GameAudios {
    return {
        audio_question: normalizeSkillBoolean(value.audio_question),
        audio_choice: normalizeSkillBoolean(value.audio_choice),
        audio_assistant_voice: normalizeSkillBoolean(value.audio_assistant_voice),
        audio_background: normalizeSkillBoolean(value.audio_background),
    };
}

function createEmptyAnswer(id: number): Answer {
    return {
        id,
        text: "",
        isCorrect: false,
        imageFileName: "",
        imageFile: null,
        imageUrl: null,
        audioFileName: "",
        audioFile: null,
        audioUrl: null,
    };
}

function createEmptyQuestion(id: number, answerIds: number[]): Question {
    return {
        id,
        text: "",
        skill_listening_speaking: false,
        skill_lang_nature: false,
        skill_reading: false,
        skill_writing: false,
        hasHint: false,
        hintText: "",
        hintImageFileName: "",
        hintImageFile: null,
        hintImageUrl: null,
        hintAudioFileName: "",
        hintAudioFile: null,
        hintAudioUrl: null,
        imageFileName: "",
        imageFile: null,
        imageUrl: null,
        audioFileName: "",
        audioFile: null,
        audioUrl: null,
        answers: answerIds.map((answerId) => createEmptyAnswer(answerId)),
    };
}

export default function CompleteTheSentence({ createdGameData, initialGameDetail, onCompleted }: CompleteTheSentenceProps) {
    const nextQuestionIdRef = useRef(2);
    const nextAnswerIdRef = useRef(3);
    const initialExerciseNameRef = useRef(createdGameData?.exercise_name ?? "");
    const initialSuggestionRef = useRef("");
    const initialDescriptionRef = useRef("");
    const initialQuestionIdsRef = useRef<Set<number>>(new Set());
    const initialChoiceIdsByQuestionRef = useRef<Map<number, Set<number>>>(new Map());
    const initialQuestionSkillsRef = useRef<Map<number, QuestionSkillFlags>>(new Map());
    const initialGameAudiosRef = useRef<GameAudios>({ ...DEFAULT_GAME_AUDIOS });

    const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(1);
    const [exerciseName, setExerciseName] = useState(createdGameData?.exercise_name ?? "");
    const [suggestion, setSuggestion] = useState("");
    const [description, setDescription] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isConfirmSaveOpen, setIsConfirmSaveOpen] = useState(false);
    const [isUpdateMode, setIsUpdateMode] = useState(Boolean(initialGameDetail?.game_info?.id));
    const [statusPopUp, setStatusPopUp] = useState<{
        title: string;
        message: string;
        type: "info" | "success" | "error";
    } | null>(null);
    const [previewIframeUrl, setPreviewIframeUrl] = useState<string | null>(null);
    const [gameAudios, setGameAudios] = useState<GameAudios>({ ...DEFAULT_GAME_AUDIOS });
    const [questions, setQuestions] = useState<Question[]>([
        createEmptyQuestion(1, [1, 2]),
    ]);

    const previewUuid = String(
        initialGameDetail?.game_info?.uuid ?? createdGameData?.uuid ?? ""
    ).trim();
    const canPreview = isUpdateMode && Boolean(previewUuid);

    useEffect(() => {
        setExerciseName(createdGameData?.exercise_name ?? "");
        initialExerciseNameRef.current = createdGameData?.exercise_name ?? "";
    }, [createdGameData?.exercise_name]);

    useEffect(() => {
        if (initialGameDetail?.game_info?.id) {
            return;
        }

        const incomingDescription = String(createdGameData?.description ?? "");
        setDescription(incomingDescription);
        initialDescriptionRef.current = incomingDescription;
    }, [createdGameData?.description, initialGameDetail?.game_info?.id]);

    const applyLoadedGameDetail = (detail: CompleteTheSentenceProps["initialGameDetail"]) => {
        if (!detail?.game_info?.id) {
            const createdDescription = String(createdGameData?.description ?? "");
            setSuggestion("");
            setDescription(createdDescription);
            initialSuggestionRef.current = "";
            initialDescriptionRef.current = createdDescription;
            initialQuestionIdsRef.current = new Set();
            initialChoiceIdsByQuestionRef.current = new Map();
            initialQuestionSkillsRef.current = new Map();
            initialGameAudiosRef.current = { ...DEFAULT_GAME_AUDIOS };
            setGameAudios({ ...DEFAULT_GAME_AUDIOS });
            setIsUpdateMode(false);
            return;
        }

        const gameInfo = detail.game_info;
        const incomingQuestions = Array.isArray(detail.questions) ? detail.questions : [];

        const incomingExerciseName = String(gameInfo.exercise_name ?? "");
        const incomingSuggestion = String(gameInfo.suggestion ?? "");
        const incomingDescription = String(gameInfo.description ?? "");

        setExerciseName(incomingExerciseName);
        setSuggestion(incomingSuggestion);
        setDescription(incomingDescription);
        initialExerciseNameRef.current = incomingExerciseName;
        initialSuggestionRef.current = incomingSuggestion;
        initialDescriptionRef.current = incomingDescription;

        const mappedQuestions: Question[] = incomingQuestions.length > 0
            ? incomingQuestions.map((question, questionIndex) => {
                const questionId = Number(question.id ?? question.no ?? questionIndex + 1);
                const questionImagePath = question.image_question ?? null;
                const questionAudioPath = question.sound_question ?? null;
                const mappedAnswers: Answer[] = Array.isArray(question.choices)
                    ? question.choices.map((choice, choiceIndex) => ({
                        id: Number(choice.id ?? choiceIndex + 1),
                        text: String(choice.choice ?? ""),
                        isCorrect: Boolean(choice.is_correct),
                        imageFileName: getFileNameFromPath(choice.image_choice ?? null),
                        imageFile: null,
                        imageUrl: buildUploadedMediaUrl(choice.image_choice ?? null),
                        audioFileName: getFileNameFromPath(choice.sound_choice ?? null),
                        audioFile: null,
                        audioUrl: buildUploadedMediaUrl(choice.sound_choice ?? null),
                    }))
                    : [];

                return {
                    id: questionId,
                    text: String(question.question ?? ""),
                    skill_listening_speaking: normalizeSkillBoolean(question.skill_listening_speaking),
                    skill_lang_nature: normalizeSkillBoolean(question.skill_lang_nature),
                    skill_reading: normalizeSkillBoolean(question.skill_reading),
                    skill_writing: normalizeSkillBoolean(question.skill_writing),
                    hasHint: Boolean(
                        String(question.hint ?? "").trim() ||
                        question.image_hint ||
                        question.sound_hint
                    ),
                    hintText: String(question.hint ?? ""),
                    hintImageFileName: getFileNameFromPath(question.image_hint ?? null),
                    hintImageFile: null,
                    hintImageUrl: buildUploadedMediaUrl(question.image_hint ?? null),
                    hintAudioFileName: getFileNameFromPath(question.sound_hint ?? null),
                    hintAudioFile: null,
                    hintAudioUrl: buildUploadedMediaUrl(question.sound_hint ?? null),
                    imageFileName: getFileNameFromPath(questionImagePath),
                    imageFile: null,
                    imageUrl: buildUploadedMediaUrl(questionImagePath),
                    audioFileName: getFileNameFromPath(questionAudioPath),
                    audioFile: null,
                    audioUrl: buildUploadedMediaUrl(questionAudioPath),
                    answers: mappedAnswers.length > 0 ? mappedAnswers : [createEmptyAnswer(1), createEmptyAnswer(2)],
                };
            })
            : [createEmptyQuestion(1, [1, 2])];

        setQuestions(mappedQuestions);
        setSelectedQuestionId(mappedQuestions[0]?.id ?? null);

        const firstQuestion = incomingQuestions[0] as Record<string, unknown> | undefined;
        const resolvedGameAudios = extractGameAudiosFromUnknown({
            audio_question: firstQuestion?.audio_question,
            audio_choice: firstQuestion?.audio_choice,
            audio_assistant_voice: firstQuestion?.audio_assistant_voice,
            audio_background: firstQuestion?.audio_background,
        });
        setGameAudios(resolvedGameAudios);
        initialGameAudiosRef.current = resolvedGameAudios;

        const nextInitialQuestionIds = collectPersistedIdsFromApiRows(
            incomingQuestions,
            (question) => {
                const questionId = Number((question as { id?: number }).id);
                return Number.isInteger(questionId) && questionId > 0 ? questionId : null;
            }
        );
        const nextInitialChoiceIdsByQuestion = new Map<number, Set<number>>();
        const nextInitialQuestionSkills = new Map<number, QuestionSkillFlags>();

        incomingQuestions.forEach((question) => {
            const record = question as { id?: number; choices?: Array<{ id?: number }> };
            const questionId = Number(record.id);
            if (!nextInitialQuestionIds.has(questionId)) {
                return;
            }

            const choices = Array.isArray(record.choices) ? record.choices : [];
            nextInitialChoiceIdsByQuestion.set(
                questionId,
                collectPersistedIdsFromApiRows(choices, (choice) => {
                    const choiceId = Number(choice.id);
                    return Number.isInteger(choiceId) && choiceId > 0 ? choiceId : null;
                })
            );

            const mappedQuestion = mappedQuestions.find((item) => item.id === questionId);
            if (mappedQuestion) {
                nextInitialQuestionSkills.set(questionId, extractQuestionSkills(mappedQuestion));
            }
        });

        initialQuestionIdsRef.current = nextInitialQuestionIds;
        initialChoiceIdsByQuestionRef.current = nextInitialChoiceIdsByQuestion;
        initialQuestionSkillsRef.current = nextInitialQuestionSkills;

        const maxQuestionId = mappedQuestions.reduce((max, question) => Math.max(max, question.id), 0);
        const maxAnswerId = mappedQuestions.reduce((max, question) => {
            const localMax = question.answers.reduce((innerMax, answer) => Math.max(innerMax, answer.id), 0);
            return Math.max(max, localMax);
        }, 0);

        nextQuestionIdRef.current = maxQuestionId + 1;
        nextAnswerIdRef.current = maxAnswerId + 1;
        setIsUpdateMode(true);
    };

    useEffect(() => {
        applyLoadedGameDetail(initialGameDetail);
    }, [initialGameDetail]);

    const addQuestion = () => {
        const newQuestionId = nextQuestionIdRef.current;
        const firstAnswerId = nextAnswerIdRef.current;
        const secondAnswerId = nextAnswerIdRef.current + 1;

        nextQuestionIdRef.current += 1;
        nextAnswerIdRef.current += 2;

        setQuestions((previous) => [
            ...previous,
            createEmptyQuestion(newQuestionId, [firstAnswerId, secondAnswerId]),
        ]);

        setSelectedQuestionId(newQuestionId);
    };

    const removeQuestion = async (questionId: number) => {
        if (isUpdateMode && initialQuestionIdsRef.current.has(questionId)) {
            const deleteQuestionResponse = await completeTheSentence.deleteQuestion({ questionId });
            if (deleteQuestionResponse?.status === "failed") {
                setStatusPopUp({
                    title: "ไม่สำเร็จ",
                    message: "ลบคำถามไม่สำเร็จ",
                    type: "error",
                });
                return;
            }
            initialQuestionIdsRef.current.delete(questionId);
            initialChoiceIdsByQuestionRef.current.delete(questionId);
            initialQuestionSkillsRef.current.delete(questionId);
        }

        setQuestions((previous) => {
            const filtered = previous.filter((question) => question.id !== questionId);

            if (filtered.length === 0) {
                setSelectedQuestionId(null);
                return filtered;
            }

            if (questionId === selectedQuestionId) {
                setSelectedQuestionId(filtered[0].id);
            }

            return filtered;
        });
    };

    const updateQuestionText = (questionId: number, value: string) => {
        setQuestions((previous) =>
            previous.map((question) =>
                question.id === questionId ? { ...question, text: value } : question
            )
        );
    };

    const enableQuestionHint = (questionId: number) => {
        setQuestions((previous) =>
            previous.map((question) =>
                question.id === questionId ? { ...question, hasHint: true } : question
            )
        );
    };

    const clearQuestionHint = (questionId: number) => {
        setQuestions((previous) =>
            previous.map((question) =>
                question.id === questionId
                    ? {
                        ...question,
                        hasHint: false,
                        hintText: "",
                        hintImageFileName: "",
                        hintImageFile: null,
                        hintImageUrl: null,
                        hintAudioFileName: "",
                        hintAudioFile: null,
                        hintAudioUrl: null,
                    }
                    : question
            )
        );
    };

    const updateQuestionHintText = (questionId: number, value: string) => {
        setQuestions((previous) =>
            previous.map((question) =>
                question.id === questionId
                    ? { ...question, hasHint: true, hintText: value }
                    : question
            )
        );
    };

    const updateQuestionSkill = (
        questionId: number,
        skillKey: QuestionSkillKey,
        checked: boolean
    ) => {
        setQuestions((previous) =>
            previous.map((question) =>
                question.id === questionId
                    ? { ...question, [skillKey]: checked }
                    : question
            )
        );
    };

    const updateGameAudio = (audioKey: GameAudiosKey, checked: boolean) => {
        setGameAudios((previous) => ({
            ...previous,
            [audioKey]: checked,
        }));
    };

    const updateAnswerText = (questionId: number, answerId: number, value: string) => {
        setQuestions((previous) =>
            previous.map((question) =>
                question.id === questionId
                    ? {
                        ...question,
                        answers: question.answers.map((answer) =>
                            answer.id === answerId ? { ...answer, text: value } : answer
                        ),
                    }
                    : question
            )
        );
    };

    const updateQuestionImageFile = (questionId: number, file: File) => {
        setQuestions((previous) =>
            previous.map((question) =>
                question.id === questionId
                    ? { ...question, imageFileName: file.name, imageFile: file, imageUrl: null }
                    : question
            )
        );
    };

    const updateQuestionAudioFile = (questionId: number, file: File) => {
        setQuestions((previous) =>
            previous.map((question) =>
                question.id === questionId
                    ? { ...question, audioFileName: file.name, audioFile: file, audioUrl: null }
                    : question
            )
        );
    };

    const updateQuestionHintImageFile = (questionId: number, file: File) => {
        setQuestions((previous) =>
            previous.map((question) =>
                question.id === questionId
                    ? {
                        ...question,
                        hasHint: true,
                        hintImageFileName: file.name,
                        hintImageFile: file,
                        hintImageUrl: null,
                    }
                    : question
            )
        );
    };

    const updateQuestionHintAudioFile = (questionId: number, file: File) => {
        setQuestions((previous) =>
            previous.map((question) =>
                question.id === questionId
                    ? {
                        ...question,
                        hasHint: true,
                        hintAudioFileName: file.name,
                        hintAudioFile: file,
                        hintAudioUrl: null,
                    }
                    : question
            )
        );
    };

    const updateAnswerImageFile = (questionId: number, answerId: number, file: File) => {
        setQuestions((previous) =>
            previous.map((question) =>
                question.id === questionId
                    ? {
                        ...question,
                        answers: question.answers.map((answer) =>
                            answer.id === answerId
                                ? { ...answer, imageFileName: file.name, imageFile: file, imageUrl: null }
                                : answer
                        ),
                    }
                    : question
            )
        );
    };

    const updateAnswerAudioFile = (questionId: number, answerId: number, file: File) => {
        setQuestions((previous) =>
            previous.map((question) =>
                question.id === questionId
                    ? {
                        ...question,
                        answers: question.answers.map((answer) =>
                            answer.id === answerId
                                ? { ...answer, audioFileName: file.name, audioFile: file, audioUrl: null }
                                : answer
                        ),
                    }
                    : question
            )
        );
    };

    const toggleAnswerCorrect = (questionId: number, answerId: number) => {
        setQuestions((previous) =>
            previous.map((question) =>
                question.id === questionId
                    ? {
                        ...question,
                        answers: question.answers.map((answer) =>
                            answer.id === answerId
                                ? { ...answer, isCorrect: !answer.isCorrect }
                                : answer
                        ),
                    }
                    : question
            )
        );
    };

    const addAnswer = (questionId: number) => {
        const newAnswerId = nextAnswerIdRef.current;
        nextAnswerIdRef.current += 1;

        setQuestions((previous) =>
            previous.map((question) => {
                if (question.id !== questionId || question.answers.length >= choiceLabels.length) {
                    return question;
                }

                return {
                    ...question,
                    answers: [
                        ...question.answers,
                        createEmptyAnswer(newAnswerId),
                    ],
                };
            })
        );
    };

    const collectUploadTargets = (sourceQuestions: Question[]) => {
        const targets: UploadTarget[] = [];

        const getTimestamp = () => Date.now();

        sourceQuestions.forEach((question, questionIndex) => {
            if (question.imageFile && !question.imageUrl) {
                const timestamp = getTimestamp();
                const ext = getFileExtension(question.imageFile.name);
                targets.push({
                    key: `q-${questionIndex}-image_question`,
                    file: question.imageFile,
                    fileName: `image_question_q${question.id}_${timestamp}${ext}`,
                    sectionName: "image_question",
                    refId: `q_${question.id}`,
                });
            }

            if (question.audioFile && !question.audioUrl) {
                const timestamp = getTimestamp();
                const ext = getFileExtension(question.audioFile.name);
                targets.push({
                    key: `q-${questionIndex}-sound_question`,
                    file: question.audioFile,
                    fileName: `sound_question_q${question.id}_${timestamp}${ext}`,
                    sectionName: "sound_question",
                    refId: `q_${question.id}`,
                });
            }

            if (question.hintImageFile && !question.hintImageUrl) {
                const timestamp = getTimestamp();
                const ext = getFileExtension(question.hintImageFile.name);
                targets.push({
                    key: `q-${questionIndex}-image_hint`,
                    file: question.hintImageFile,
                    fileName: `image_hint_q${question.id}_${timestamp}${ext}`,
                    sectionName: "image_hint",
                    refId: `q_${question.id}`,
                });
            }

            if (question.hintAudioFile && !question.hintAudioUrl) {
                const timestamp = getTimestamp();
                const ext = getFileExtension(question.hintAudioFile.name);
                targets.push({
                    key: `q-${questionIndex}-sound_hint`,
                    file: question.hintAudioFile,
                    fileName: `sound_hint_q${question.id}_${timestamp}${ext}`,
                    sectionName: "sound_hint",
                    refId: `q_${question.id}`,
                });
            }

            question.answers.forEach((answer, answerIndex) => {
                if (answer.imageFile && !answer.imageUrl) {
                    const timestamp = getTimestamp();
                    const ext = getFileExtension(answer.imageFile.name);
                    targets.push({
                        key: `q-${questionIndex}-a-${answerIndex}-image_choice`,
                        file: answer.imageFile,
                        fileName: `image_choice_q${question.id}_c${answer.id}_${timestamp}${ext}`,
                        sectionName: "image_choice",
                        refId: `choice_${question.id}_${answer.id}`,
                    });
                }

                if (answer.audioFile && !answer.audioUrl) {
                    const timestamp = getTimestamp();
                    const ext = getFileExtension(answer.audioFile.name);
                    targets.push({
                        key: `q-${questionIndex}-a-${answerIndex}-sound_choice`,
                        file: answer.audioFile,
                        fileName: `sound_choice_q${question.id}_c${answer.id}_${timestamp}${ext}`,
                        sectionName: "sound_choice",
                        refId: `choice_${question.id}_${answer.id}`,
                    });
                }
            });
        });

        return targets;
    };

    const uploadPendingMedia = async (targets: UploadTarget[]) => {
        const uploadedUrlByKey = new Map<string, string>();

        if (targets.length === 0) {
            return uploadedUrlByKey;
        }

        const uploadResults = await Promise.allSettled(
            targets.map((target) =>
                uploadAPI
                    .uploadMedia({
                        file: target.file,
                        fileName: target.fileName,
                        sectionName: target.sectionName,
                        ref_id: target.refId,
                    })
                    .then((response) => ({ target, response }))
            )
        );

        for (const result of uploadResults) {
            if (result.status === "rejected") {
                throw new Error("upload_failed");
            }

            const response = result.value.response as { status?: string; url?: string };
            if (response?.status === "failed" || !response?.url) {
                throw new Error("upload_failed");
            }

            uploadedUrlByKey.set(result.value.target.key, response.url);
        }

        return uploadedUrlByKey;
    };

    const applyUploadedUrlsToState = (sourceQuestions: Question[], uploadedUrlByKey: Map<string, string>) => {
        return sourceQuestions.map((question, questionIndex) => ({
            ...question,
            imageUrl: uploadedUrlByKey.get(`q-${questionIndex}-image_question`) ?? question.imageUrl,
            imageFile: uploadedUrlByKey.get(`q-${questionIndex}-image_question`) ? null : question.imageFile,
            audioUrl: uploadedUrlByKey.get(`q-${questionIndex}-sound_question`) ?? question.audioUrl,
            audioFile: uploadedUrlByKey.get(`q-${questionIndex}-sound_question`) ? null : question.audioFile,
            hintImageUrl: uploadedUrlByKey.get(`q-${questionIndex}-image_hint`) ?? question.hintImageUrl,
            hintImageFile: uploadedUrlByKey.get(`q-${questionIndex}-image_hint`) ? null : question.hintImageFile,
            hintAudioUrl: uploadedUrlByKey.get(`q-${questionIndex}-sound_hint`) ?? question.hintAudioUrl,
            hintAudioFile: uploadedUrlByKey.get(`q-${questionIndex}-sound_hint`) ? null : question.hintAudioFile,
            answers: question.answers.map((answer, answerIndex) => ({
                ...answer,
                imageUrl: uploadedUrlByKey.get(`q-${questionIndex}-a-${answerIndex}-image_choice`) ?? answer.imageUrl,
                imageFile: uploadedUrlByKey.get(`q-${questionIndex}-a-${answerIndex}-image_choice`) ? null : answer.imageFile,
                audioUrl: uploadedUrlByKey.get(`q-${questionIndex}-a-${answerIndex}-sound_choice`) ?? answer.audioUrl,
                audioFile: uploadedUrlByKey.get(`q-${questionIndex}-a-${answerIndex}-sound_choice`) ? null : answer.audioFile,
            })),
        }));
    };

    const removeAnswer = async (questionId: number, answerId: number) => {
        if (isUpdateMode) {
            const initialChoiceIds = initialChoiceIdsByQuestionRef.current.get(questionId);
            if (initialChoiceIds?.has(answerId)) {
                const deleteChoiceResponse = await completeTheSentence.deleteChoice({
                    questionId,
                    choiceIds: [answerId],
                });
                if (deleteChoiceResponse?.status === "failed") {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: "ลบตัวเลือกไม่สำเร็จ",
                        type: "error",
                    });
                    return;
                }
                initialChoiceIds.delete(answerId);
            }
        }

        setQuestions((previous) =>
            previous.map((question) =>
                question.id === questionId
                    ? {
                        ...question,
                        answers: question.answers.filter((answer) => answer.id !== answerId),
                    }
                    : question
            )
        );
    };

    const moveQuestion = (questionId: number, direction: -1 | 1) => {
        setQuestions((previous) => {
            const currentIndex = previous.findIndex((question) => question.id === questionId);

            if (currentIndex < 0) {
                return previous;
            }

            const nextIndex = currentIndex + direction;
            if (nextIndex < 0 || nextIndex >= previous.length) {
                return previous;
            }

            const reordered = [...previous];
            const [movedQuestion] = reordered.splice(currentIndex, 1);
            reordered.splice(nextIndex, 0, movedQuestion);
            return reordered;
        });
    };

    const moveQuestionUp = (questionId: number) => {
        moveQuestion(questionId, -1);
    };

    const moveQuestionDown = (questionId: number) => {
        moveQuestion(questionId, 1);
    };

    const handleSubmitCompleteTheSentence = async () => {
        if (!createdGameData?.id) {
            setStatusPopUp({
                title: "ไม่สำเร็จ",
                message: "ไม่พบข้อมูลเกมที่สร้างล่าสุด",
                type: "error",
            });
            return;
        }

        try {
            setIsSubmitting(true);

            const trimmedExerciseName = exerciseName.trim();
            const trimmedSuggestion = suggestion.trim();
            const trimmedDescription = description.trim();
            const shouldUpdateExerciseName =
                trimmedExerciseName !== initialExerciseNameRef.current.trim();
            const shouldUpdateSuggestion =
                trimmedSuggestion !== initialSuggestionRef.current.trim();
            const shouldUpdateDescription =
                trimmedDescription !== initialDescriptionRef.current.trim();

            if (shouldUpdateExerciseName || shouldUpdateSuggestion || shouldUpdateDescription) {
                const updateResponse = await completeTheSentence.updateExerciseNameAndSuggestion({
                    gameId: createdGameData.id,
                    ...(shouldUpdateExerciseName ? { exercise_name: trimmedExerciseName } : {}),
                    ...(shouldUpdateSuggestion ? { suggestion: trimmedSuggestion } : {}),
                    ...(shouldUpdateDescription ? { description: trimmedDescription } : {}),
                });

                if (updateResponse?.status === "failed") {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: "อัปเดตชื่อกิจกรรม คำแนะนำ หรือรายละเอียดไม่สำเร็จ",
                        type: "error",
                    });
                    return;
                }

                initialExerciseNameRef.current = trimmedExerciseName;
                initialSuggestionRef.current = trimmedSuggestion;
                initialDescriptionRef.current = trimmedDescription;
                setExerciseName(trimmedExerciseName);
                setSuggestion(trimmedSuggestion);
                setDescription(trimmedDescription);
            }

            const uploadTargets = collectUploadTargets(questions);
            const uploadedUrlByKey = await uploadPendingMedia(uploadTargets);
            const questionsWithResolvedUrls = applyUploadedUrlsToState(questions, uploadedUrlByKey);

            if (!Array.isArray(questionsWithResolvedUrls) || questionsWithResolvedUrls.length === 0) {
                setStatusPopUp({
                    title: "ไม่สำเร็จ",
                    message: "กรุณาเพิ่มคำถามอย่างน้อย 1 ข้อ",
                    type: "error",
                });
                return;
            }

            const payloadQuestions: Array<{
                no: number;
                question: string;
                hint: string | null;
                image_question: string | null;
                sound_question: string | null;
                image_hint: string | null;
                sound_hint: string | null;
                skill_listening_speaking: boolean;
                skill_lang_nature: boolean;
                skill_reading: boolean;
                skill_writing: boolean;
                audio_question: boolean;
                audio_choice: boolean;
                audio_assistant_voice: boolean;
                audio_background: boolean;
                choices: Array<{
                    choice: string;
                    is_correct: boolean;
                    image_choice: string | null;
                    sound_choice: string | null;
                }>;
            }> = [];
            const normalizedGameAudios = extractGameAudiosFromUnknown(gameAudios);

            for (let questionIndex = 0; questionIndex < questionsWithResolvedUrls.length; questionIndex += 1) {
                const question = questionsWithResolvedUrls[questionIndex];
                const normalizedQuestionText = question.text.trim();
                const normalizedHintText = question.hasHint ? question.hintText.trim() : "";
                const normalizedSkills = extractQuestionSkills(question);

                const hasQuestionMedia =
                    Boolean(normalizedQuestionText) ||
                    Boolean(question.imageUrl) ||
                    Boolean(question.audioUrl);
                if (!hasQuestionMedia) {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: `คำถามข้อที่ ${questionIndex + 1} ต้องมีอย่างน้อย ข้อความ, รูป หรือเสียง`,
                        type: "error",
                    });
                    return;
                }

                if (!Array.isArray(question.answers) || question.answers.length === 0) {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: `คำถามข้อที่ ${questionIndex + 1} ต้องมีคำตอบอย่างน้อย 1 ตัวเลือก`,
                        type: "error",
                    });
                    return;
                }

                const normalizedChoices: Array<{
                    choice: string;
                    is_correct: boolean;
                    image_choice: string | null;
                    sound_choice: string | null;
                }> = [];

                for (let answerIndex = 0; answerIndex < question.answers.length; answerIndex += 1) {
                    const answer = question.answers[answerIndex];
                    const normalizedChoiceText = answer.text.trim();

                    const hasChoiceMedia =
                        Boolean(normalizedChoiceText) ||
                        Boolean(answer.imageUrl) ||
                        Boolean(answer.audioUrl);
                    if (!hasChoiceMedia) {
                        setStatusPopUp({
                            title: "ไม่สำเร็จ",
                            message: `ตัวเลือกที่ ${answerIndex + 1} ของคำถามข้อที่ ${questionIndex + 1} ต้องมีอย่างน้อย ข้อความ, รูป หรือเสียง`,
                            type: "error",
                        });
                        return;
                    }

                    normalizedChoices.push({
                        choice: normalizedChoiceText,
                        is_correct: normalizeSkillBoolean(answer.isCorrect),
                        image_choice: answer.imageUrl,
                        sound_choice: answer.audioUrl,
                    });
                }

                payloadQuestions.push({
                    no: questionIndex + 1,
                    question: normalizedQuestionText,
                    hint: normalizedHintText || null,
                    image_question: question.imageUrl,
                    sound_question: question.audioUrl,
                    image_hint: question.hasHint ? question.hintImageUrl : null,
                    sound_hint: question.hasHint ? question.hintAudioUrl : null,
                    skill_listening_speaking: normalizedSkills.skill_listening_speaking,
                    skill_lang_nature: normalizedSkills.skill_lang_nature,
                    skill_reading: normalizedSkills.skill_reading,
                    skill_writing: normalizedSkills.skill_writing,
                    audio_question: normalizedGameAudios.audio_question,
                    audio_choice: normalizedGameAudios.audio_choice,
                    audio_assistant_voice: normalizedGameAudios.audio_assistant_voice,
                    audio_background: normalizedGameAudios.audio_background,
                    choices: normalizedChoices,
                });

            }
            const response = await completeTheSentence.createCompleteTheSentenceChoice({
                gameId: createdGameData.id,
                questions: payloadQuestions,
            });

            if (response?.status === "failed") {
                setStatusPopUp({
                    title: "ไม่สำเร็จ",
                    message: "บันทึก Complete The Sentence ไม่สำเร็จ",
                    type: "error",
                });
                return;
            }

            setQuestions(questionsWithResolvedUrls);

            if (previewUuid) {
                const latestGameDetail = await mainAPI.getGameinfoAndQuestionByUuid(previewUuid);
                if (latestGameDetail && (latestGameDetail as { status?: string }).status !== "failed") {
                    applyLoadedGameDetail(latestGameDetail as CompleteTheSentenceProps["initialGameDetail"]);
                } else {
                    setIsUpdateMode(true);
                    initialQuestionIdsRef.current = new Set(
                        questionsWithResolvedUrls.map((question) => question.id)
                    );
                    initialChoiceIdsByQuestionRef.current = new Map(
                        questionsWithResolvedUrls.map((question) => [
                            question.id,
                            new Set(question.answers.map((answer) => answer.id)),
                        ])
                    );
                    initialQuestionSkillsRef.current = new Map(
                        questionsWithResolvedUrls.map((question) => [
                            question.id,
                            extractQuestionSkills(question),
                        ])
                    );
                    initialGameAudiosRef.current = normalizedGameAudios;
                }
            } else {
                setIsUpdateMode(true);
                initialQuestionIdsRef.current = new Set(
                    questionsWithResolvedUrls.map((question) => question.id)
                );
                initialChoiceIdsByQuestionRef.current = new Map(
                    questionsWithResolvedUrls.map((question) => [
                        question.id,
                        new Set(question.answers.map((answer) => answer.id)),
                    ])
                );
                initialQuestionSkillsRef.current = new Map(
                    questionsWithResolvedUrls.map((question) => [
                        question.id,
                        extractQuestionSkills(question),
                    ])
                );
                initialGameAudiosRef.current = normalizedGameAudios;
            }

            setStatusPopUp(buildGameSuccessStatus("Complete the Sentence", "create"));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateCompleteTheSentence = async () => {
        if (!createdGameData?.id) {
            setStatusPopUp({
                title: "ไม่สำเร็จ",
                message: "ไม่พบข้อมูลเกมที่ต้องการอัปเดต",
                type: "error",
            });
            return;
        }

        try {
            setIsSubmitting(true);

            const trimmedExerciseName = exerciseName.trim();
            const trimmedSuggestion = suggestion.trim();
            const trimmedDescription = description.trim();
            const shouldUpdateExerciseName =
                trimmedExerciseName !== initialExerciseNameRef.current.trim();
            const shouldUpdateSuggestion =
                trimmedSuggestion !== initialSuggestionRef.current.trim();
            const shouldUpdateDescription =
                trimmedDescription !== initialDescriptionRef.current.trim();

            if (shouldUpdateExerciseName || shouldUpdateSuggestion || shouldUpdateDescription) {
                const updateResponse = await completeTheSentence.updateExerciseNameAndSuggestion({
                    gameId: createdGameData.id,
                    ...(shouldUpdateExerciseName ? { exercise_name: trimmedExerciseName } : {}),
                    ...(shouldUpdateSuggestion ? { suggestion: trimmedSuggestion } : {}),
                    ...(shouldUpdateDescription ? { description: trimmedDescription } : {}),
                });

                if (updateResponse?.status === "failed") {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: "อัปเดตชื่อกิจกรรม คำแนะนำ หรือรายละเอียดไม่สำเร็จ",
                        type: "error",
                    });
                    return;
                }

                initialExerciseNameRef.current = trimmedExerciseName;
                initialSuggestionRef.current = trimmedSuggestion;
                initialDescriptionRef.current = trimmedDescription;
                setExerciseName(trimmedExerciseName);
                setSuggestion(trimmedSuggestion);
                setDescription(trimmedDescription);
            }

            const uploadTargets = collectUploadTargets(questions);
            const uploadedUrlByKey = await uploadPendingMedia(uploadTargets);
            const questionsWithResolvedUrls = applyUploadedUrlsToState(questions, uploadedUrlByKey);
            let questionsWithSyncedChoiceIds = questionsWithResolvedUrls;
            const currentGameAudios = extractGameAudiosFromUnknown(gameAudios);
            const initialGameAudios = initialGameAudiosRef.current;
            const changedAudioPayload: Partial<GameAudios> = {};
            if (initialGameAudios.audio_question !== currentGameAudios.audio_question) {
                changedAudioPayload.audio_question = currentGameAudios.audio_question;
            }
            if (initialGameAudios.audio_choice !== currentGameAudios.audio_choice) {
                changedAudioPayload.audio_choice = currentGameAudios.audio_choice;
            }
            if (initialGameAudios.audio_assistant_voice !== currentGameAudios.audio_assistant_voice) {
                changedAudioPayload.audio_assistant_voice = currentGameAudios.audio_assistant_voice;
            }
            if (initialGameAudios.audio_background !== currentGameAudios.audio_background) {
                changedAudioPayload.audio_background = currentGameAudios.audio_background;
            }

            const currentQuestionIds = new Set<number>(
                questionsWithResolvedUrls.map((question) => question.id)
            );

            const deletedQuestionIds = Array.from(initialQuestionIdsRef.current).filter(
                (questionId) => !currentQuestionIds.has(questionId)
            );

            for (const questionId of deletedQuestionIds) {
                const deleteQuestionResponse = await completeTheSentence.deleteQuestion({
                    questionId,
                });

                if (deleteQuestionResponse?.status === "failed") {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: `ลบคำถาม id ${questionId} ไม่สำเร็จ`,
                        type: "error",
                    });
                    return;
                }
            }

            for (const question of questionsWithResolvedUrls) {
                const initialChoiceIds = initialChoiceIdsByQuestionRef.current.get(question.id);
                if (!initialChoiceIds || initialChoiceIds.size === 0) {
                    continue;
                }

                const currentChoiceIds = new Set<number>(question.answers.map((answer) => answer.id));
                const deletedChoiceIds = Array.from(initialChoiceIds).filter(
                    (choiceId) => !currentChoiceIds.has(choiceId)
                );

                if (deletedChoiceIds.length === 0) {
                    continue;
                }

                const deleteChoiceResponse = await completeTheSentence.deleteChoice({
                    questionId: question.id,
                    choiceIds: deletedChoiceIds,
                });

                if (deleteChoiceResponse?.status === "failed") {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: `ลบตัวเลือกของคำถาม id ${question.id} ไม่สำเร็จ`,
                        type: "error",
                    });
                    return;
                }
            }

            for (let questionIndex = 0; questionIndex < questionsWithResolvedUrls.length; questionIndex += 1) {
                const question = questionsWithResolvedUrls[questionIndex];
                const isExistingQuestion = initialQuestionIdsRef.current.has(question.id);
                if (!isExistingQuestion) {
                    continue;
                }

                const normalizedHintText = question.hasHint ? question.hintText.trim() : "";
                const currentSkills = extractQuestionSkills(question);
                const initialSkills = initialQuestionSkillsRef.current.get(question.id);
                const changedSkillPayload: Partial<QuestionSkillFlags> = {};

                if (!initialSkills || initialSkills.skill_listening_speaking !== currentSkills.skill_listening_speaking) {
                    changedSkillPayload.skill_listening_speaking = currentSkills.skill_listening_speaking;
                }
                if (!initialSkills || initialSkills.skill_lang_nature !== currentSkills.skill_lang_nature) {
                    changedSkillPayload.skill_lang_nature = currentSkills.skill_lang_nature;
                }
                if (!initialSkills || initialSkills.skill_reading !== currentSkills.skill_reading) {
                    changedSkillPayload.skill_reading = currentSkills.skill_reading;
                }
                if (!initialSkills || initialSkills.skill_writing !== currentSkills.skill_writing) {
                    changedSkillPayload.skill_writing = currentSkills.skill_writing;
                }

                const updateQuestionResponse = await completeTheSentence.updateQuestion({
                    questionId: question.id,
                    no: questionIndex + 1,
                    question: question.text,
                    hint: normalizedHintText || null,
                    image_question: question.imageUrl || null,
                    sound_question: question.audioUrl || null,
                    image_hint: question.hasHint ? question.hintImageUrl || null : null,
                    sound_hint: question.hasHint ? question.hintAudioUrl || null : null,
                    ...changedSkillPayload,
                    ...changedAudioPayload,
                });

                if (updateQuestionResponse?.status === "failed") {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: `อัปเดตคำถามข้อที่ ${questionIndex + 1} ไม่สำเร็จ`,
                        type: "error",
                    });
                    return;
                }

                const initialChoiceIds = initialChoiceIdsByQuestionRef.current.get(question.id) ?? new Set<number>();
                const existingChoicesPayload = question.answers
                    .filter((answer) => initialChoiceIds.has(answer.id))
                    .map((answer) => ({
                        id: answer.id,
                        choice: answer.text,
                        is_correct: answer.isCorrect,
                        image_choice: answer.imageUrl || null,
                        sound_choice: answer.audioUrl || null,
                    }));

                if (existingChoicesPayload.length > 0) {
                    const updateChoiceResponse = await completeTheSentence.updateChoice({
                        questionId: question.id,
                        choices: existingChoicesPayload,
                    });

                    if (updateChoiceResponse?.status === "failed") {
                        setStatusPopUp({
                            title: "ไม่สำเร็จ",
                            message: `อัปเดตคำตอบเดิมของคำถามข้อที่ ${questionIndex + 1} ไม่สำเร็จ`,
                            type: "error",
                        });
                        return;
                    }
                }

                const newChoiceAnswers = question.answers
                    .filter((answer) => !initialChoiceIds.has(answer.id));

                const newChoicesPayload = newChoiceAnswers
                    .map((answer) => ({
                        choice: answer.text,
                        is_correct: answer.isCorrect,
                        image_choice: answer.imageUrl || null,
                        sound_choice: answer.audioUrl || null,
                    }));

                if (newChoicesPayload.length > 0) {
                    const createChoiceResponse = await completeTheSentence.createChoice({
                        questionId: question.id,
                        choices: newChoicesPayload,
                    });

                    if (
                        createChoiceResponse &&
                        typeof createChoiceResponse === "object" &&
                        "status" in createChoiceResponse &&
                        createChoiceResponse.status === "failed"
                    ) {
                        setStatusPopUp({
                            title: "ไม่สำเร็จ",
                            message: `เพิ่มคำตอบใหม่ในคำถามข้อที่ ${questionIndex + 1} ไม่สำเร็จ`,
                            type: "error",
                        });
                        return;
                    }

                    const createdChoices = Array.isArray((createChoiceResponse as { choices?: Array<{ id?: number }> }).choices)
                        ? (createChoiceResponse as { choices: Array<{ id?: number }> }).choices
                        : [];

                    if (createdChoices.length !== newChoiceAnswers.length) {
                        setStatusPopUp({
                            title: "ไม่สำเร็จ",
                            message: `รูปแบบข้อมูลคำตอบใหม่ของคำถามข้อที่ ${questionIndex + 1} ไม่ถูกต้อง`,
                            type: "error",
                        });
                        return;
                    }

                    const createdIdByTempId = new Map<number, number>();
                    for (let index = 0; index < newChoiceAnswers.length; index += 1) {
                        const createdId = Number(createdChoices[index]?.id);
                        if (!Number.isInteger(createdId) || createdId <= 0) {
                            setStatusPopUp({
                                title: "ไม่สำเร็จ",
                                message: `ไม่พบ id ของคำตอบใหม่ในคำถามข้อที่ ${questionIndex + 1}`,
                                type: "error",
                            });
                            return;
                        }

                        createdIdByTempId.set(newChoiceAnswers[index].id, createdId);
                    }

                    questionsWithSyncedChoiceIds = questionsWithSyncedChoiceIds.map((questionItem) => {
                        if (questionItem.id !== question.id) {
                            return questionItem;
                        }

                        return {
                            ...questionItem,
                            answers: questionItem.answers.map((answer) => {
                                const syncedId = createdIdByTempId.get(answer.id);
                                return syncedId ? { ...answer, id: syncedId } : answer;
                            }),
                        };
                    });
                }
            }

            const newQuestionsPayload = questionsWithSyncedChoiceIds
                .map((question, questionIndex) => ({ question, questionIndex }))
                .filter(({ question }) => !initialQuestionIdsRef.current.has(question.id))
                .map(({ question, questionIndex }) => {
                    const normalizedHintText = question.hasHint ? question.hintText.trim() : "";
                    const normalizedSkills = extractQuestionSkills(question);
                    return {
                        no: questionIndex + 1,
                        question: question.text,
                        hint: normalizedHintText || null,
                        image_question: question.imageUrl || null,
                        sound_question: question.audioUrl || null,
                        image_hint: question.hasHint ? question.hintImageUrl || null : null,
                        sound_hint: question.hasHint ? question.hintAudioUrl || null : null,
                        skill_listening_speaking: normalizedSkills.skill_listening_speaking,
                        skill_lang_nature: normalizedSkills.skill_lang_nature,
                        skill_reading: normalizedSkills.skill_reading,
                        skill_writing: normalizedSkills.skill_writing,
                        audio_question: currentGameAudios.audio_question,
                        audio_choice: currentGameAudios.audio_choice,
                        audio_assistant_voice: currentGameAudios.audio_assistant_voice,
                        audio_background: currentGameAudios.audio_background,
                        choices: question.answers.map((answer) => ({
                            choice: answer.text,
                            is_correct: answer.isCorrect,
                            image_choice: answer.imageUrl || null,
                            sound_choice: answer.audioUrl || null,
                        })),
                    };
                });

            if (newQuestionsPayload.length > 0) {
                const createNewQuestionsResponse = await completeTheSentence.createCompleteTheSentenceChoice({
                    gameId: createdGameData.id,
                    questions: newQuestionsPayload,
                });

                if (createNewQuestionsResponse?.status === "failed") {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: "เพิ่มคำถามใหม่ไม่สำเร็จ",
                        type: "error",
                    });
                    return;
                }

                if (previewUuid) {
                    const latestGameDetail = await mainAPI.getGameinfoAndQuestionByUuid(previewUuid);
                    if (latestGameDetail && (latestGameDetail as { status?: string }).status !== "failed") {
                        applyLoadedGameDetail(latestGameDetail as CompleteTheSentenceProps["initialGameDetail"]);
                        initialGameAudiosRef.current = currentGameAudios;
                        setStatusPopUp(buildGameSuccessStatus("Complete the Sentence", "update"));
                        setIsUpdateMode(true);
                        return;
                    }
                }
            }

            setQuestions(questionsWithSyncedChoiceIds);

            initialQuestionIdsRef.current = new Set(
                questionsWithSyncedChoiceIds.map((question) => question.id)
            );
            initialChoiceIdsByQuestionRef.current = new Map(
                questionsWithSyncedChoiceIds.map((question) => [
                    question.id,
                    new Set(question.answers.map((answer) => answer.id)),
                ])
            );
            initialQuestionSkillsRef.current = new Map(
                questionsWithSyncedChoiceIds.map((question) => [
                    question.id,
                    extractQuestionSkills(question),
                ])
            );
            initialGameAudiosRef.current = currentGameAudios;

            setStatusPopUp(buildGameSuccessStatus("Complete the Sentence", "update"));
            setIsUpdateMode(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseStatusPopUp = () => {
        setStatusPopUp(null);
        void onCompleted;
    };

    const handleClickFinishButton = () => {
        if (isSubmitting) {
            return;
        }

        setIsConfirmSaveOpen(true);
    };

    const handleCancelConfirmSave = () => {
        setIsConfirmSaveOpen(false);
    };

    const handleConfirmSave = () => {
        setIsConfirmSaveOpen(false);
        if (isUpdateMode) {
            void handleUpdateCompleteTheSentence();
            return;
        }

        void handleSubmitCompleteTheSentence();
    };

    const handleOpenPreview = () => {
        if (!canPreview) {
            return;
        }

        if (!previewUuid) {
            setStatusPopUp({
                title: "ไม่สามารถ Preview ได้",
                message: "ไม่พบ UUID ของเกมสำหรับการ Preview",
                type: "error",
            });
            return;
        }

        const previewBaseUrl = "https://thaipilagame.gforcesolution.com";
        const normalizedBaseUrl = previewBaseUrl.replace(/\/$/, "");
        const previewUrl = `${normalizedBaseUrl}/${encodeURIComponent(previewUuid)}?preview`;
        setPreviewIframeUrl(previewUrl);
    };

    const handleClosePreview = () => {
        setPreviewIframeUrl(null);
    };

    return (
        <section className="mx-auto w-full rounded px-4 py-3 text-[#3f3f3f] lg:px-10">
            <div className="border-b border-[#dddddd] pb-2 text-2xl leading-none">
                <span className="font-semibold text-[#4E4E4E]">แก้ไขเนื้อหา &gt; </span>
                <span className="font-bold text-[#2b2b2b]">Complete the Sentence</span>
            </div>

            <div className="mt-4 space-y-3">
                <div>
                    <label className="mb-1 block text-lg font-medium text-[#5b5b5b]">ชื่อกิจกรรม</label>
                    <input
                        value={exerciseName}
                        onChange={(event) => setExerciseName(event.target.value)}
                        className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
                    />
                </div>

                <div>
                    <label className="mb-1 flex items-center gap-1 text-lg font-medium text-[#5b5b5b]">
                        คำแนะนำในการเล่น
                        <FiInfo size={14} />
                    </label>
                    <input
                        value={suggestion}
                        onChange={(event) => setSuggestion(event.target.value)}
                        className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
                        placeholder="เลือกคำตอบที่ถูกต้อง"
                    />
                </div>

                <div>
                    <label className="mb-1 flex items-center gap-1 text-lg font-medium text-[#5b5b5b]">
                        Description / รายละเอียด
                    </label>
                    <input
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
                        placeholder="เลือกคำตอบที่ถูกต้อง"
                    />
                </div>

            </div>

            <div className="mt-4 space-y-3">
                <div className="grid grid-cols-[40px_1fr_1fr_60px] items-center gap-4">
                    <span className="text-lg font-medium text-[#5a5a5a] text-center">ลำดับ</span>
                </div>

                {questions.map((question, index) => (
                    <QuestionBlock
                        key={question.id}
                        index={index + 1}
                        question={question}
                        onQuestionTextChange={updateQuestionText}
                        onQuestionHintTextChange={updateQuestionHintText}
                        onQuestionHintImageFileChange={updateQuestionHintImageFile}
                        onQuestionHintAudioFileChange={updateQuestionHintAudioFile}
                        onEnableQuestionHint={enableQuestionHint}
                        onClearQuestionHint={clearQuestionHint}
                        onQuestionImageFileChange={updateQuestionImageFile}
                        onQuestionAudioFileChange={updateQuestionAudioFile}
                        onAnswerTextChange={updateAnswerText}
                        onAnswerImageFileChange={updateAnswerImageFile}
                        onAnswerAudioFileChange={updateAnswerAudioFile}
                        onToggleAnswerCorrect={toggleAnswerCorrect}
                        onAddAnswer={addAnswer}
                        onRemoveAnswer={removeAnswer}
                        onRemoveQuestion={removeQuestion}
                        onMoveQuestionUp={moveQuestionUp}
                        onMoveQuestionDown={moveQuestionDown}
                        onQuestionSkillChange={updateQuestionSkill}
                        onSelectQuestion={setSelectedQuestionId}
                        isSelected={question.id === selectedQuestionId}
                        canMoveUp={index > 0}
                        canMoveDown={index < questions.length - 1}
                    />
                ))}
            </div>

            <div className="mt-4 flex items-center justify-between">
                <button
                    type="button"
                    onClick={addQuestion}
                    className="ml-12 cursor-pointer rounded border border-[#adadad] bg-[#e7e7e7] px-3 py-1.5 text-lg font-semibold text-[#535353] hover:bg-[#d6d6d6]"
                >
                    + เพิ่มคำถาม
                </button>
                <div className="mr-10 flex items-center gap-3">
                    <button
                        type="button"
                        onClick={handleOpenPreview}
                        disabled={!canPreview}
                        className="cursor-pointer rounded bg-[#4E4E4E] px-7 py-2 text-lg font-semibold text-white shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:bg-[#3d3d3d] disabled:cursor-not-allowed disabled:bg-[#9a9a9a] disabled:shadow-none"
                    >
                        Preview
                    </button>
                    <button
                        type="button"
                        onClick={handleClickFinishButton}
                        disabled={isSubmitting}
                        className={`cursor-pointer rounded px-7 py-2 text-lg font-semibold text-white shadow-[0_2px_0_rgba(0,0,0,0.2)] disabled:cursor-not-allowed disabled:opacity-70 ${isUpdateMode ? "bg-[#1e4fa3] hover:bg-[#183f82]" : "bg-[#f4d735] text-[#4a4a4a] hover:bg-[#e0c91a]"}`}
                    >
                        {isSubmitting ? "กำลังบันทึก..." : isUpdateMode ? "อัพเดท" : "บันทึก"}
                    </button>
                </div>
            </div>

            <div className="p-4 ml-12">
                <div className="grid grid-cols-2 gap-x-8">

                    <div className="flex flex-col">
                        <label className="text-sm font-bold text-[#555] mb-3">เสียงบรรยาย</label>
                        <div className="flex flex-col gap-4">
                            <label className="flex items-center gap-2 text-sm font-medium text-[#444] cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={gameAudios.audio_question}
                                    onChange={(event) => updateGameAudio("audio_question", event.target.checked)}
                                    className="h-4 w-4 rounded border-[#ccc]"
                                />
                                ปิดเสียงบรรยายโจทย์
                            </label>

                            <label className="flex items-center gap-2 text-sm font-medium text-[#444] cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={gameAudios.audio_choice}
                                    onChange={(event) => updateGameAudio("audio_choice", event.target.checked)}
                                    className="h-4 w-4 rounded border-[#ccc]"
                                />
                                ปิดเสียงบรรยายตัวเลือก
                            </label>
                        </div>
                    </div>

                    <div className="flex flex-col">
                        <label className="text-sm font-bold text-[#555] mb-3">เสียงเกม</label>
                        <div className="flex flex-col gap-4">
                            <label className="flex items-center gap-2 text-sm font-medium text-[#444] cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={gameAudios.audio_assistant_voice}
                                    onChange={(event) => updateGameAudio("audio_assistant_voice", event.target.checked)}
                                    className="h-4 w-4 rounded border-[#ccc]"
                                />
                                ปิดเสียงผู้ช่วย
                            </label>

                            <label className="flex items-center gap-2 text-sm font-medium text-[#444] cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={gameAudios.audio_background}
                                    onChange={(event) => updateGameAudio("audio_background", event.target.checked)}
                                    className="h-4 w-4 rounded border-[#ccc]"
                                />
                                ปิดเสียงพื้นหลัง
                            </label>
                        </div>
                    </div>

                </div>
            </div>

            {isConfirmSaveOpen ? (
                <ConfirmPopUp
                    title={isUpdateMode ? "ยืนยันการอัพเดท" : "ยืนยันการบันทึก"}
                    message={isUpdateMode ? "ต้องการอัพเดทข้อมูลหรือไม่" : "ต้องการบันทึกข้อมูลหรือไม่"}
                    cancelText="ยกเลิก"
                    confirmText={isUpdateMode ? "อัพเดท" : "ยืนยัน"}
                    onCancel={handleCancelConfirmSave}
                    onConfirm={handleConfirmSave}
                    isConfirming={isSubmitting}
                />
            ) : null}

            {statusPopUp ? (
                <StatusPopUp
                    title={statusPopUp.title}
                    message={statusPopUp.message}
                    type={statusPopUp.type}
                    onClose={handleCloseStatusPopUp}
                />
            ) : null}

            {previewIframeUrl ? (
                <GamePreviewModal
                    src={previewIframeUrl}
                    iframeTitle="Complete the Sentence Preview"
                    onClose={handleClosePreview}
                    closeVariant="danger"
                    shellVariant="editor"
                />
            ) : null}
        </section>
    );
}
